import type { SimConfig } from '../config/simConfig';
import type { PitchCondition } from '../types';
import { DT } from '../config/simConfig';

// D44 · P0-A — distance-calibrated pass power (single source of truth for every bot pass site).
// A bot pass launched with a FIXED charge (the old PASS_CHARGE = 6) leaves the boot at ~11 m/s and,
// under the §5.3 drag model (KD_AIR quadratic + KF_ROLL viscous), dies to walking pace over 15–20 m —
// "slow and never reaches me". `passChargeFor` inverts the flight: it returns the charge TICKS whose
// launch delivers the ball to the receiver still travelling at ≥ cfg.ai.PASS_ARRIVE_SPEED (crisp but
// trappable, well under CLAIM_MAX_SPEED). Pure + deterministic; mirrors sim/trajectory.ts / physics/ball.

/** Ground-pass launch speed for a given charge (PASS button: no loft ⇒ flat roll). */
function launchSpeed(chargeTicks: number, k: SimConfig['actions']['kick']): number {
  const curve = Math.pow(Math.min(chargeTicks, k.CHARGE_CAP) / k.CHARGE_CAP, k.CHARGE_EXP);
  return k.KICK_MIN + (k.MAX.PASS - k.KICK_MIN) * curve;
}

/** Speed (m/s) the ball still carries after rolling `distM` from launch, under the same drag model as
 *  physics/ball.ts (1-D: drag is isotropic, a flat pass stays grounded, so lateral is irrelevant to the
 *  arrival speed along the pass line). Returns the low tail speed if the ball stops short of distM. */
function arrivalSpeed(chargeTicks: number, distM: number, cfg: SimConfig, conditions: PitchCondition): number {
  const k = cfg.actions.kick;
  const cond = cfg.conditions[conditions];
  let v = launchSpeed(chargeTicks, k);
  let x = 0;
  for (let t = 0; t < 240; t++) {
    // grounded: air drag (−KD_AIR·|v|·v) + rolling resistance (−KF_ROLL·v)
    const a = -(cfg.ball.KD_AIR * v + cond.KF_ROLL) * v;
    v += a * DT;
    if (v <= 0) return 0;
    x += v * DT;
    if (x >= distM) return v;
  }
  return v;
}

/** Charge ticks so the pass ARRIVES at a receiver `distM` away with pace (≥ cfg.ai.PASS_ARRIVE_SPEED),
 *  computed against the §5.3 drag model. Monotone in distance; bisected (arrival speed is monotone in
 *  charge) and clamped to [floor, CHARGE_CAP]. */
export function passChargeFor(distM: number, cfg: SimConfig, conditions: PitchCondition): number {
  const k = cfg.actions.kick;
  const target = cfg.ai.PASS_ARRIVE_SPEED;
  const FLOOR = 2; // a pass always commits ≥ 2 ticks (D39: leaves no window for a tackle to cancel it)
  // even a full-power pass can't deliver `target` pace this far ⇒ hit it as hard as we can
  if (arrivalSpeed(k.CHARGE_CAP, distM, cfg, conditions) < target) return k.CHARGE_CAP;
  // bisect for the LEAST charge whose arrival speed ≥ target (fewer ticks = quicker release, less cancel window)
  let lo = 0;
  let hi = k.CHARGE_CAP;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (arrivalSpeed(mid, distM, cfg, conditions) >= target) hi = mid;
    else lo = mid;
  }
  return Math.max(FLOOR, Math.min(k.CHARGE_CAP, Math.ceil(hi)));
}
